import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Phone, Mail, MessageCircle, FileDown, ZoomIn } from "lucide-react";
import { publicApi, fileUrl, PLACEHOLDER_IMG, API } from "@/lib/api";
import { CATEGORY_MAP } from "@/data/categories";
import { TECHNICAL_IMAGE_INDEXES } from "@/data/technicalSheets";
import QuoteDialog from "@/components/QuoteDialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import DOMPurify from 'dompurify';

const isHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);

export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedTechImage, setSelectedTechImage] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);  useEffect(() => {
    setLoading(true);
    publicApi.get(`/products/${productId}`)
      .then((res) => setProduct(res.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h2 className="font-display text-3xl uppercase tracking-tight">Producto no encontrado</h2>
        <Link to="/catalogo" className="inline-block mt-6 bg-amber-500 text-slate-950 px-6 py-3 font-display font-bold uppercase tracking-wider btn-brutal" data-testid="back-to-catalog-btn">
          Volver al catálogo
        </Link>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [];
  const technicalIndexes = new Set(TECHNICAL_IMAGE_INDEXES[product.name] || []);
  const galleryImages = images.filter((_, index) => index > 0 && !technicalIndexes.has(index));
  const technicalImages = images.filter((_, index) => technicalIndexes.has(index));
  const activeImg = galleryImages.length ? fileUrl(galleryImages[activeIdx]?.storage_path) : PLACEHOLDER_IMG;
  const catName = CATEGORY_MAP[product.category]?.name || product.category;
  const waMessage = `Hola, me interesa la maquinaria *${product.name}* (${catName}). ¿Me pueden enviar más información y cotización?`;
  const waUrl = `https://wa.me/56961018000?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link to="/catalogo" data-testid="back-catalog-link" className="inline-flex items-center gap-2 font-display font-bold uppercase text-xs tracking-wider text-slate-500 hover:text-slate-950 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver al catálogo
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] bg-white border-2 border-slate-950 overflow-hidden">
              <img src={activeImg} alt={product.name} onError={(e)=>{e.currentTarget.src=PLACEHOLDER_IMG;}} className="w-full h-full object-contain p-3" />
            </div>
            {galleryImages.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {galleryImages.map((img, idx) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveIdx(idx)}
                    data-testid={`gallery-thumb-${idx}`}
                    className={`aspect-square overflow-hidden border-2 ${idx === activeIdx ? "border-amber-500" : "border-slate-200 hover:border-slate-400"}`}
                  >
                    <img src={fileUrl(img.storage_path)} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <Link to={`/catalogo/${product.category}`} className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600 hover:underline">
              {catName}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mt-2 leading-tight">
              {product.name}
            </h1>
            {product.short_description && (
              <p className="mt-4 text-slate-600 leading-relaxed">{product.short_description}</p>
            )}

            <div className="mt-8 bg-slate-950 text-white p-6 border-2 border-slate-950">
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-500 mb-2">Cotización</div>
              <p className="text-slate-300 text-sm leading-relaxed">
                El precio se entrega por cotización personalizada según especificación y logística.
                Completa el formulario y nuestro equipo te responderá a la brevedad.
              </p>
              <button
                onClick={() => setQuoteOpen(true)}
                data-testid="product-cotizar-btn"
                className="mt-5 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-4 text-base btn-brutal inline-flex items-center justify-between"
              >
                Solicitar cotización
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="product-whatsapp-btn"
                className="mt-3 w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-display font-black uppercase tracking-wider px-6 py-4 text-sm inline-flex items-center justify-between transition-colors border-2 border-[#25D366]"
              >
                <span className="inline-flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Hablar por WhatsApp
                </span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href={`${API}/products/${product.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="product-pdf-btn"
                className="mt-3 w-full bg-transparent hover:bg-white/10 text-white font-display font-bold uppercase tracking-wider px-6 py-3 text-xs inline-flex items-center justify-between transition-colors border-2 border-white/40 hover:border-white"
              >
                <span className="inline-flex items-center gap-2">
                  <FileDown className="w-4 h-4" /> Descargar ficha técnica PDF
                </span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <a href="tel:+56961018000" className="flex items-center gap-2 border-2 border-slate-200 p-3 hover:border-slate-950 transition-colors">
                <Phone className="w-4 h-4 text-amber-500" />
                <span className="font-mono">+569 6101 8000</span>
              </a>
              <a href="mailto:contacto@gwmlatam.com" className="flex items-center gap-2 border-2 border-slate-200 p-3 hover:border-slate-950 transition-colors">
                <Mail className="w-4 h-4 text-amber-500" />
                <span className="font-mono text-xs truncate">Email comercial</span>
              </a>
            </div>
          </div>
        </div>

        {/* Description & specs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600 mb-3">Descripción</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4">Sobre este equipo</h2>
            {product.description ? (
              isHtml(product.description) ? (
                <div
                  className="rich-content text-slate-700"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
                />
              ) : (
                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">{product.description}</div>
              )
            ) : (
              <div className="text-slate-500 italic">Sin descripción detallada disponible.</div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600 mb-3">Especificaciones</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4">Ficha técnica</h2>
            {(product.specs && product.specs.length > 0) || technicalImages.length > 0 ? (
              <div className="space-y-4">
                {product.specs && product.specs.length > 0 && (
                  <dl className="border-2 border-slate-950 bg-white">
                    {product.specs.map((s, i) => (
                      <div key={i} className={`grid grid-cols-2 px-4 py-3 ${i % 2 === 0 ? "bg-slate-50" : "bg-white"} ${i < product.specs.length - 1 ? "border-b border-slate-200" : ""}`}>
                        <dt className="font-display font-bold uppercase text-xs tracking-wider text-slate-700">{s.key}</dt>
                        <dd className="font-mono text-sm text-slate-950">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {technicalImages.map((img, i) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedTechImage(img)}
                    className="group relative block w-full bg-white border border-slate-200 p-2 cursor-zoom-in hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    aria-label={`Abrir ficha técnica de ${product.name}${technicalImages.length > 1 ? ` ${i + 1}` : ""} en tamaño grande`}
                  >
                    <img
                      src={fileUrl(img.storage_path)}
                      alt={`Ficha técnica de ${product.name}${technicalImages.length > 1 ? ` ${i + 1}` : ""}`}
                      className="w-full h-auto object-contain"
                      loading="lazy"
                    />
                    <span className="absolute right-4 bottom-4 inline-flex items-center gap-2 bg-slate-950/90 text-white px-3 py-2 text-xs font-display font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                      <ZoomIn className="w-4 h-4" /> Ver en grande
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">Especificaciones disponibles bajo cotización.</p>
            )}
          </div>
        </div>
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={product} />
      <Dialog open={!!selectedTechImage} onOpenChange={(open) => !open && setSelectedTechImage(null)}>
        <DialogContent className="max-w-[96vw] w-[96vw] max-h-[96vh] overflow-auto rounded-none border-0 bg-white p-3">
          <DialogTitle className="sr-only">Ficha técnica de {product.name}</DialogTitle>
          {selectedTechImage && (
            <img
              src={fileUrl(selectedTechImage.storage_path)}
              alt={`Ficha técnica ampliada de ${product.name}`}
              className="block max-w-full max-h-[90vh] w-auto h-auto mx-auto object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
